import { cn, focusRing } from "@simpro/ui";
import { User } from "lucide-react";
import { NavLink } from "react-router";
import { auth } from "@/lib/api";
import { initials } from "@/lib/initials";

/**
 * The way to the account: the customer's initials once signed in, "Sign in" before. A new customer's
 * name is their masked number until they set one, and initials of "+91••••" say nothing, so they get
 * an icon instead.
 */
export function AccountButton({ compact = false }: { compact?: boolean }) {
  const signedIn = auth.useIsSignedIn();
  const { data: customer } = auth.useCurrentCustomer();

  const look = ({ isActive }: { isActive: boolean }) =>
    cn(
      "inline-flex shrink-0 items-center justify-center rounded-full font-semibold transition-colors",
      isActive ? "bg-ink text-on-ink" : "bg-surface text-heading ring-1 ring-edge-subtle ring-inset hover:ring-edge",
      focusRing,
    );

  if (!signedIn) {
    // The folded phone header has no room for the word; the person icon says the same thing there.
    if (compact) {
      return (
        <NavLink to="/account" aria-label="Sign in" className={(state) => cn(look(state), "size-9")}>
          <User className="size-4" aria-hidden="true" />
        </NavLink>
      );
    }
    return (
      <NavLink
        to="/account"
        className={(state) => cn(look(state), "h-11 px-5 text-sm")}
      >
        Sign in
      </NavLink>
    );
  }

  const name = customer?.displayName;
  const named = name !== undefined && name !== customer?.phone;

  return (
    <NavLink
      to="/account"
      aria-label={named ? `Your account, ${name}` : "Your account"}
      className={(state) =>
        cn(look(state), compact ? "size-9 text-xs" : "size-11 text-sm")
      }
    >
      {named ? initials(name) : <User className="size-4" aria-hidden="true" />}
    </NavLink>
  );
}
